/**
 * Race Analysis Service — Segment metrics (Velocity, SR, DPS)
 *
 * Consumes /api/vukz/biomechanics/race endpoints for segment-based
 * race analysis (Modelo Minas TC).
 */
import { AZURE } from './api';

/**
 * Fetch segment chart data for an athlete's latest (or specific) race.
 *
 * @param {number} athleteId - Athlete ID
 * @param {Object} [opts]
 * @param {number} [opts.raceId] - Specific race_id (optional)
 * @param {string} [opts.style]  - Filter by swimming style
 * @returns {Promise<{velocity, stroke_rate, dps, race_info, all_races}|null>}
 */
export const getSegmentCharts = async (athleteId, opts = {}) => {
  try {
    const params = {};
    if (opts.raceId) params.race_id = opts.raceId;
    if (opts.style) params.style = opts.style;

    const { data } = await AZURE.get(
      `/vukz/biomechanics/race/athlete/${athleteId}/segment-charts`,
      { params }
    );
    return data?.data || null;
  } catch (error) {
    console.error('[RaceAnalysis] getSegmentCharts error:', error);
    return null;
  }
};

/**
 * Fetch full race report (segments + key_times + laps + pacing).
 *
 * @param {number} raceId
 * @returns {Promise<Object|null>}
 */
export const getRaceReport = async (raceId) => {
  try {
    const { data } = await AZURE.get(
      `/vukz/biomechanics/race/races/${raceId}`
    );
    return data?.data || null;
  } catch (error) {
    console.error('[RaceAnalysis] getRaceReport error:', error);
    return null;
  }
};

/**
 * Compare athlete's races for same event (historical progression).
 *
 * @param {number} athleteId
 * @param {Object} [opts]
 * @param {string} [opts.event]
 * @param {string} [opts.style]
 * @param {number} [opts.limit]
 * @returns {Promise<Object|null>}
 */
export const getAthleteComparison = async (athleteId, opts = {}) => {
  try {
    const params = {};
    if (opts.event) params.event = opts.event;
    if (opts.style) params.style = opts.style;
    if (opts.limit) params.limit = opts.limit;

    const { data } = await AZURE.get(
      `/vukz/biomechanics/race/athlete/${athleteId}/compare`,
      { params }
    );
    return data || null;
  } catch (error) {
    console.error('[RaceAnalysis] getAthleteComparison error:', error);
    return null;
  }
};
